/* The browser's half of a call.
 *
 * WHAT THIS DOES. Asks for the microphone, makes an offer, hands it to our
 * backend, applies the answer that comes back. After that the audio is
 * between this browser and the media server directly — nothing of it goes
 * through our backend, which relays exactly two messages and then leaves
 * the path.
 *
 * TWO TRAPS, both of which cost other people time before us.
 *
 *   addTrack already creates the transceiver. Calling addTransceiver as
 *   well produces an offer with two audio sections, which the service
 *   refuses by name rather than guessing which one is meant.
 *
 *   ICE gathering must finish before the offer is sent. Sending a
 *   half-gathered offer means advertising candidates the far side never
 *   sees, and the symptom is a connection that never establishes rather
 *   than an error anybody can read.
 *
 * EVERYBODY IS CONNECTED TO SEND, BUT ONLY THE FLOOR ACTUALLY SENDS.
 * Every seat is in the mix — a leg outside it hears nothing — so the mixer
 * cannot be what keeps a listener quiet. This browser is. The microphone
 * is attached from the start and the sender is emptied until the server
 * says this person may be heard, so promotion is a local switch with
 * nothing to renegotiate, and until then nothing of theirs leaves the
 * machine.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'

export type AudioState =
  | 'idle'
  | 'asking'          // waiting for the microphone prompt
  | 'connecting'
  | 'settling'       // on the floor, not yet safe to speak
  | 'live'
  | 'blocked'         // they said no, or there is no microphone
  | 'unreachable'     // the network would not let us through
  | 'reconnecting'    // was connected, lost it, connecting again
  | 'failed'

export function useCallAudio(
  callId: string | null,
  enabled: boolean,
  /* THE SERVICE'S HANDLE FOR THIS BROWSER. Null means our audio is not
     connected as far as the server is concerned — which is the state
     after leaving and coming back, because a leg belongs to a connection
     and the old one went with the old visit. Reconnecting on that is what
     makes a rejoin work; without it somebody returns to the room, appears
     present, and can never be put on the floor. */
  legId: string | null = null,
  /* WHETHER THE SERVER LETS THIS PERSON BE HEARD: on the floor and not
     turned off by whoever runs the call. Everyone else sends nothing. */
  mayBeHeard = false,
) {
  const [state, setState] = useState<AudioState>('idle')
  /* MUTING YOURSELF NEVER REACHES A SERVER. It is between a person and
     their own microphone — we disable the track and nothing else knows,
     which is also why it cannot be undone by a moderator. */
  const [muted, setMuted] = useState(false)
  /* The browser has the audio and will not play it. Distinct from having
     no audio at all, and fixable by the person: one click releases it. */
  const [blockedPlayback, setBlockedPlayback] = useState(false)
  /* They have no microphone, or said no. They can still hear. */
  const [micDenied, setMicDenied] = useState(false)
  const peer = useRef<RTCPeerConnection | null>(null)
  const mic = useRef<MediaStream | null>(null)
  const remote = useRef<HTMLAudioElement | null>(null)
  const started = useRef(false)
  /* The handle the service gave US, as distinct from the one the polled
     room state reports — they disagree for a few seconds after a join. */
  const ourLeg = useRef<string | null>(null)
  /* When our current leg was established, so a poll answered before it
     cannot be read as that leg having gone. */
  const joinedAt = useRef(0)
  /* Tries left after the server refused to connect the sound. */
  const retries = useRef(0)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /* MUTING DETACHES THE TRACK, it does not merely disable it.
   *
   * `track.enabled = false` is the familiar form and it is the weaker one:
   * the specification says the track produces silence, and the sender is
   * free to keep transmitting it. A mixer that receives a stream of
   * silence has something to decode, so the leg stays in the mix
   * contributing almost-nothing — which is nothing at all with one muted
   * person and an audible noise floor with fifty.
   *
   * Replacing the sender's track with null stops the RTP outright. The far
   * side decodes nothing, so the leg is excluded from the mix rather than
   * summed as zeros. The microphone itself is left running, so unmuting is
   * instant and needs no new permission prompt.
   *
   * TWO THINGS SILENCE A PERSON and either is enough: their own button, and
   * the server not having given them the floor. The second is the one that
   * matters most, because it covers everybody listening — a hundred people
   * who never touched a button, each of whom would otherwise be an open
   * microphone in the room.
   */
  const silent = muted || !mayBeHeard
  const silentRef = useRef(silent)
  silentRef.current = silent

  const applySilence = useCallback(() => {
    const now = silentRef.current
    const track = mic.current?.getAudioTracks()[0] ?? null
    const sender = peer.current
      ?.getSenders()
      .find((s) => s.track?.kind === 'audio' || s.track === null)
    if (sender && sender.track !== (now ? null : track)) {
      void sender.replaceTrack(now ? null : track)
    }
    // Belt and braces: a browser that ignores replaceTrack(null) still
    // sees a disabled track.
    if (track) track.enabled = !now
  }, [])

  const toggleMute = useCallback(() => setMuted((was) => !was), [])

  const stop = useCallback(() => {
    clearTimeout(retryTimer.current)
    retries.current = 0
    peer.current?.close()
    peer.current = null
    mic.current?.getTracks().forEach((t) => t.stop())
    mic.current = null
    remote.current?.remove()
    remote.current = null
    started.current = false
    ourLeg.current = null
    setState('idle')
    setMuted(false)
  }, [])

  const start = useCallback(async () => {
    if (!callId || started.current) return
    started.current = true

    /* A LISTENER NEEDS NO MICROPHONE. Declining it used to abandon the
       whole connection, so somebody who said no — or had no microphone at
       all — could not hear the call either, while the screen told them
       they could. Listening is the common case and it is one-way; we
       connect regardless and simply have nothing to send. */
    /* A RECONNECT KEEPS THE MICROPHONE IT HAS. Asking again leaked the
       previous stream — the browser kept showing the microphone in use —
       and could put a second permission prompt in front of somebody whose
       sound had just dropped. */
    const held = mic.current
    let stream: MediaStream | null =
      held && held.getAudioTracks().some((t) => t.readyState === 'live')
        ? held
        : null
    if (!stream) {
      /* Cleared BEFORE asking, never after. It used to be cleared on the
         line after the refusal set it, so a refused microphone was never
         reported and somebody given the floor was told everyone could hear
         them. */
      setMicDenied(false)
      try {
        setState('asking')
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch {
        setMicDenied(true)
      }
    }
    mic.current = stream

    setBlockedPlayback(false)
    const pc = new RTCPeerConnection({
      // The service is ICE-lite and advertises one candidate, so there is
      // no STUN to consult and nothing for a relay to do.
      iceServers: [],
    })
    peer.current = pc

    /* One transceiver, created by addTrack. See the note above. With no
       microphone we still need a receiving one, or the answer carries
       audio into a connection with nowhere to put it. */
    if (stream) {
      const s = stream
      s.getAudioTracks().forEach((track) => pc.addTrack(track, s))
      // Silent from the first packet, not from the first poll.
      applySilence()
    } else {
      pc.addTransceiver('audio', { direction: 'recvonly' })
    }

    /* THE ELEMENT HAS TO BE IN THE DOCUMENT. A detached `new Audio()`
       holds the track perfectly well and several browsers simply decline
       to play it — no error anybody sees, just silence that looks exactly
       like nobody talking. It is hidden rather than absent.

       WE ALSO ASK FOR THE TRACK, not just offer ours. Without a receiving
       transceiver the answer may carry audio we never surface. */
    pc.ontrack = (event) => {
      let el = remote.current
      if (!el) {
        el = document.createElement('audio')
        el.autoplay = true
        el.setAttribute('playsinline', '')
        el.style.display = 'none'
        document.body.appendChild(el)
        remote.current = el
      }
      el.srcObject = event.streams[0] ?? new MediaStream([event.track])
      void el.play().catch((err) => {
        /* Autoplay policy. The click that opened this room is usually
           enough; when it is not, say so rather than sitting silent —
           this is the difference between "nobody is speaking" and "your
           browser will not play it". */
        // eslint-disable-next-line no-console
        console.warn('call audio blocked', err)
        setBlockedPlayback(true)
      })
    }

    /* NEVER CONNECTED AND LOST ARE DIFFERENT FAILURES.
     *
     * Never connected is the network refusing us — a firewall that blocks
     * UDP — and trying again will not change it, so we say so.
     *
     * Connected and then lost is the far side going: the media server
     * restarting takes every room's sound with it and tells nobody. The
     * server has no idea either, so nothing else will ever prompt a
     * reconnect. We do it ourselves — and the new offer is what finds out
     * the room has gone and moves the call into a fresh one.
     *
     * "disconnected" often heals by itself within a second or two, so it
     * gets a few seconds before it counts; "failed" counts at once. */
    let wasConnected = false
    let lostTimer: ReturnType<typeof setTimeout> | undefined
    const reconnect = () => {
      if (peer.current !== pc) return
      clearTimeout(lostTimer)
      pc.close()
      peer.current = null
      started.current = false
      ourLeg.current = null
      setState('reconnecting')
      void start()
    }
    pc.oniceconnectionstatechange = () => {
      const ice = pc.iceConnectionState
      if (ice === 'connected' || ice === 'completed') {
        wasConnected = true
        clearTimeout(lostTimer)
        return
      }
      if (!wasConnected) {
        if (ice === 'failed') setState('unreachable')
        return
      }
      if (ice === 'failed') reconnect()
      if (ice === 'disconnected') {
        clearTimeout(lostTimer)
        lostTimer = setTimeout(reconnect, 4000)
      }
    }

    try {
      setState('connecting')
      const offer = await pc.createOffer({ offerToReceiveAudio: true })
      await pc.setLocalDescription(offer)
      await gathered(pc)

      const answer = await api.post<{ participant_id: string; sdp: string }>(
        `/me/calls/${callId}/audio`,
        { sdp: pc.localDescription?.sdp ?? '' },
      )
      await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp })
      ourLeg.current = answer.participant_id || null
      joinedAt.current = Date.now()
      setState('live')
    } catch {
      /* NOT THE NETWORK, AND NOT FINAL. This is the server or the sound
         service refusing for a moment — a blocked network shows up as the
         connection failing after this point, never here. So the half-made
         connection is closed and the whole thing tried again shortly,
         a few times, rather than leaving somebody on a screen that has
         given up. */
      pc.close()
      if (peer.current === pc) peer.current = null
      started.current = false
      setState('failed')
      if (retries.current < 5) {
        retries.current += 1
        const delay = 3000 * retries.current
        retryTimer.current = setTimeout(() => void start(), delay)
      }
      return
    }
    retries.current = 0
  }, [callId, applySilence])

  /* Whenever either switch moves, or a new connection replaces the old
     one, the sender follows. */
  useEffect(() => {
    applySilence()
  }, [silent, state, applySilence])

  /* Turning it off has to hang up. The cleanup form of this read the
     value from the render BEFORE the change, so going from on to off never
     stopped anything — a call that ended kept its microphone open. */
  useEffect(() => {
    if (enabled) void start()
    else if (started.current) stop()
  }, [enabled, start, stop])

  /* Reconnect when the server says we have no leg but we believe we are
     live — a rejoin, or a connection the service dropped.
     
     THE HANDLE WE WERE GIVEN IS THE ONE THAT COUNTS. Room state arrives on
     a poll, so for a few seconds after connecting it still reports the leg
     we had before — null on a first join. Acting on that reconnects a
     connection that just succeeded, and each attempt leaves its
     predecessor behind on the service: within a minute the room holds a
     dozen legs for one person, the floor names only the newest, and the
     browser may be publishing on an older one. Which is silence that
     looks exactly like a working call.
     
     So we compare against what the join actually returned, and only treat
     a null as real once the poll has caught up to it. */
  useEffect(() => {
    if (!enabled || !callId) return
    if (state !== 'live') return
    // Nothing to compare against yet.
    if (!ourLeg.current) return
    // The poll still reports the leg we hold: everything is in order.
    if (legId === ourLeg.current) return

    /* A POLL ANSWERED BEFORE WE JOINED IS NOT EVIDENCE. Room state is
       fetched every few seconds, so a request already in flight when the
       join completed returns the state from BEFORE it — a null leg for
       somebody who has just connected. Acting on that reconnects a
       working connection, which makes the next poll stale in turn.
       Waiting out one polling interval is what makes the null mean
       "the service dropped us" rather than "the answer predates us". */
    if (Date.now() - joinedAt.current < 8000) return

    // The poll has caught up and says that leg is gone.
    if (legId === null) {
      ourLeg.current = null
      started.current = false
      peer.current?.close()
      peer.current = null
      void start()
    }
  }, [legId, state, enabled, callId, start])

  useEffect(() => stop, [stop])

  /* One click anywhere releases a blocked element, so the fix is offered
     rather than explained. */
  const releasePlayback = useCallback(() => {
    void remote.current?.play().then(() => setBlockedPlayback(false))
  }, [])

  return {
    state,
    muted,
    toggleMute,
    stop,
    blockedPlayback,
    releasePlayback,
    micDenied,
  }
}

/** Wait for ICE gathering, or give up and send what we have.
 *
 *  A browser that never reports complete would otherwise hang here for
 *  ever. Three seconds is far longer than a local gather takes and far
 *  shorter than anybody's patience.
 */
function gathered(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check)
      clearTimeout(timer)
      resolve()
    }
    const check = () => {
      if (pc.iceGatheringState === 'complete') done()
    }
    const timer = setTimeout(done, 3000)
    pc.addEventListener('icegatheringstatechange', check)
  })
}
